import { useState } from "react";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import { Lock, Check, X, Loader2, Shield } from "lucide-react";

export default function SetPasswordPrompt({ onDismiss }) {
  const { auth, setAuth } = useAppContext();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Don't show if user already has a password
  if (auth?.user?.has_password) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true); setError("");
    try {
      await api.setPassword(auth.jwt, password);
      setAuth({ ...auth, user: { ...auth.user, has_password: true } });
      localStorage.setItem("pigeon-auth", JSON.stringify({ ...auth, user: { ...auth.user, has_password: true } }));
      setDone(true);
      setTimeout(() => onDismiss?.(), 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to set password");
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="bg-lime-50 border border-lime-300 rounded-xl p-4 flex items-center gap-3" data-testid="password-set-success">
        <Check className="w-5 h-5 text-lime-600" />
        <p className="text-xs font-bold text-lime-700">Password set! You can now sign in with email + password.</p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="set-password-prompt">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-600" />
          <span className="text-xs font-black text-amber-700 uppercase tracking-wider">Set a Password</span>
        </div>
        <button onClick={onDismiss} className="p-1 rounded-lg hover:bg-amber-100 text-amber-400" data-testid="dismiss-password-prompt">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-amber-600 mb-3">Set a password to quickly sign in next time without a magic link.</p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" />
          <input
            data-testid="set-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 6 chars)"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:border-amber-500 outline-none"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" />
          <input
            data-testid="set-password-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm password"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:border-amber-500 outline-none"
          />
        </div>
        {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
        <button
          data-testid="set-password-submit"
          type="submit"
          disabled={loading}
          className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 transition-all"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Set Password</>}
        </button>
      </form>
    </div>
  );
}
